import React, { useEffect, useState } from "react";

const SUNRISE_MINUTES = 6 * 60 + 30;
const DAY_START_MINUTES = 9 * 60;
const SUNSET_START_MINUTES = 18 * 60;
const SUNSET_END_MINUTES = 21 * 60;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getArcPosition(progress, startX = 8, endX = 92, baseY = 72, arcHeight = 54) {
  const safeProgress = clamp(progress, 0, 1);
  const x = startX + (endX - startX) * safeProgress;
  const y = baseY - Math.sin(Math.PI * safeProgress) * arcHeight;

  return { x, y };
}

function getSkyData() {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  const dayProgress = clamp(
    (minutes - SUNRISE_MINUTES) / (SUNSET_END_MINUTES - SUNRISE_MINUTES),
    0,
    1
  );
  const nightDuration = 24 * 60 - SUNSET_END_MINUTES + SUNRISE_MINUTES;
  const nightProgress =
    minutes >= SUNSET_END_MINUTES
      ? (minutes - SUNSET_END_MINUTES) / nightDuration
      : (minutes + (24 * 60 - SUNSET_END_MINUTES)) / nightDuration;

  let period = "night";
  if (minutes >= SUNRISE_MINUTES && minutes < DAY_START_MINUTES) period = "morning";
  else if (minutes >= DAY_START_MINUTES && minutes < SUNSET_START_MINUTES) period = "day";
  else if (minutes >= SUNSET_START_MINUTES && minutes < SUNSET_END_MINUTES) period = "evening";

  const sun = getArcPosition(dayProgress, 7, 93, 76, 58);
  const moon = getArcPosition(nightProgress, 7, 93, 70, 48);
  const twilightProgress =
    period === "evening"
      ? clamp((minutes - SUNSET_START_MINUTES) / (SUNSET_END_MINUTES - SUNSET_START_MINUTES), 0, 1)
      : period === "morning"
      ? 1 - clamp((minutes - SUNRISE_MINUTES) / (DAY_START_MINUTES - SUNRISE_MINUTES), 0, 1)
      : period === "night"
      ? 1
      : 0;

  return {
    now,
    period,
    sun,
    moon,
    showSun: period !== "night",
    showMoon: period === "night" || period === "evening",
    starOpacity: clamp(twilightProgress, 0, 1),
  };
}

function HomeEphemerideSky({ ephemeride }) {
  const [timeData, setTimeData] = useState(getSkyData());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeData(getSkyData());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  if (!ephemeride) return null;

  return (
    <section
      className={`ephemeride-card ${timeData.period}`}
      style={{
        "--sun-x": `${timeData.sun.x}%`,
        "--sun-y": `${timeData.sun.y}%`,
        "--moon-x": `${timeData.moon.x}%`,
        "--moon-y": `${timeData.moon.y}%`,
        "--star-opacity": timeData.starOpacity,
      }}
    >
      <div className="ephemeride-sky" aria-hidden="true">
        <div className="ephemeride-stars ephemeride-stars-one" />
        <div className="ephemeride-stars ephemeride-stars-two" />
        <div className="ephemeride-horizon" />
        <div className={`ephemeride-sun ${timeData.showSun ? "is-visible" : ""}`} />
        <div className={`ephemeride-moon ${timeData.showMoon ? "is-visible" : ""}`} />
        <div className="ephemeride-cloud cloud-one" />
        <div className="ephemeride-cloud cloud-two" />
        <div className="ephemeride-cloud cloud-three" />
        <div className="ephemeride-birds birds-one">
          <span />
          <span />
          <span />
        </div>
        <div className="ephemeride-birds birds-two">
          <span />
          <span />
        </div>
      </div>

      <div className="ephemeride-content">
        <div className="ephemeride-date">
          {timeData.now.toLocaleDateString("fr-FR", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>

        <h2 className="ephemeride-title">F&ecirc;te du jour</h2>
        <div className="ephemeride-main">{ephemeride.name}</div>
      </div>
    </section>
  );
}

export default HomeEphemerideSky;
